import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Trash2, UserPlus } from 'lucide-react';
import { setDoc, collection, doc, updateDoc, serverTimestamp, arrayUnion } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/features/auth/AuthProvider';
import { useToast } from '@/hooks/useToast';
import { useGamification } from '@/hooks/useGamification';
import { useTranslation } from 'react-i18next';
import { PlanSelect } from '@/components/common/PlanSelect';
import { usePublicPlans } from '@/hooks/usePublicPlans';
import { requestedPlanOf } from '@/lib/plans';

interface KidData {
    name: string;
    age: string;
    belt: string;
    gender: string;
    type: 'child' | 'adult' | 'myself';
    /** The plan asked for THIS child — a request, not a purchase. Each child may differ. */
    planId: string;
}

interface AddKidsStepProps {
    onNext: (kidIds?: string[]) => void;
}

export const AddKidsStep = ({ onNext }: AddKidsStepProps) => {
    const { t } = useTranslation();
    const { currentUser } = useAuth();
    const { success, error: showError } = useToast();
    const { checkChallenge } = useGamification();
    const { plans, loading: plansLoading, failed: plansFailed, retry: retryPlans } = usePublicPlans();
    const [kids, setKids] = useState<KidData[]>([{ name: '', age: '', belt: '', gender: '', type: 'child', planId: '' }]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const updateKid = (index: number, field: keyof KidData, value: string) => {
        const newKids = [...kids];
        const currentKid = newKids[index];

        // Auto-fill for 'myself'
        if (field === 'type' && value === 'myself') {
            newKids[index] = {
                ...currentKid,
                type: 'myself',
                name: currentUser?.displayName || '',
                // If user already has these fields in auth profile, we could pre-fill, 
                // but usually we don't have extended fields in auth object yet.
                // We rely on user inputting them to update the profile.
            };
        } else {
            newKids[index] = { ...currentKid, [field]: value };
        }

        setKids(newKids);
    };

    const addKidRow = () => {
        setKids([...kids, { name: '', age: '', belt: '', gender: '', type: 'child', planId: '' }]);
    };

    const removeKidRow = (index: number) => {
        const newKids = kids.filter((_, i) => i !== index);
        setKids(newKids);
    };

    const handleSubmit = async () => {
        // Validate
        const validKids = kids.filter(k => k.name.trim() !== '');
        if (validKids.length === 0) {
            showError(t('onboarding.kids.error_add_member'));
            return;
        }

        // Validate complete fields (Age only required for kids/adults, maybe optional for myself depending on logic, but typically required for training profile)
        const incomplete = validKids.some(k => !k.age || !k.belt || !k.gender);
        if (incomplete) {
            showError(t('onboarding.kids.error_complete_fields'));
            return;
        }

        // Each child's plan is asked for separately — two children may want different plans.
        if (validKids.some(k => !k.planId)) {
            showError(t('signup.plan.required'));
            return;
        }

        setIsSubmitting(true);
        try {
            const promises = validKids.map(async (kid) => {
                if (kid.type === 'myself' && currentUser) {
                    // Update Current User
                    const userRef = doc(db, 'users', currentUser.uid);
                    await updateDoc(userRef, {
                        displayName: kid.name,
                        age: parseInt(kid.age) || null,
                        belt: kid.belt,
                        gender: kid.gender as any,
                        isStudent: true, // Mark parent as student
                        ...requestedPlanOf(plans.find(p => p.id === kid.planId) ?? null),
                        updatedAt: serverTimestamp(),
                    });
                    return { id: currentUser.uid };
                } else {
                    // Create New User - use setDoc to ensure doc exists before gamification
                    // Generate ID first, then set doc (avoids race condition with checkChallenge)
                    const newUserRef = doc(collection(db, 'users'));
                    await setDoc(newUserRef, {
                        displayName: kid.name,
                        role: 'kid', // Managed profile
                        isKidProfile: true, // Managed by parent
                        // Distinguish 'adult kid' vs 'child' only by age data, system treats as managed profile
                        age: parseInt(kid.age) || null,
                        belt: kid.belt,
                        gender: kid.gender as any,
                        parentId: currentUser?.uid,
                        status: 'pending',
                        createdAt: serverTimestamp(),
                        ...requestedPlanOf(plans.find(p => p.id === kid.planId) ?? null),
                        totalScore: 0, // Initialize score field
                        // Generate gender-appropriate happy avatar
                        // Blue background for boys, pink for girls
                        photoURL: kid.gender === 'male'
                            ? `https://api.dicebear.com/7.x/thumbs/svg?seed=${kid.name}&backgroundColor=60a5fa&shapeColor=ffffff`
                            : `https://api.dicebear.com/7.x/thumbs/svg?seed=${kid.name}&backgroundColor=f472b6&shapeColor=ffffff`,
                    });
                    return { id: newUserRef.id };
                }
            });

            const results = await Promise.all(promises);
            const kidIds = results.map(res => res.id);

            // Denormalize the new kids' UIDs onto the parent doc's
            // `childrenIds` array. Used by useFamily as the primary lookup
            // (more reliable than the kid → parentId query, which we
            // observed silently returning 0 results for at least one
            // production parent on Jun 1 2026). Non-blocking: if this fails
            // we still have the parentId backreference on each kid as a
            // safety net.
            if (currentUser?.uid && kidIds.length > 0) {
                try {
                    await updateDoc(doc(db, 'users', currentUser.uid), {
                        childrenIds: arrayUnion(...kidIds),
                        updatedAt: serverTimestamp(),
                    });
                } catch (denormErr) {
                    console.warn('Failed to denormalize childrenIds on parent doc:', denormErr);
                }
            }

            // Award XP for account creation (best effort - don't fail onboarding if gamification fails)
            for (const kidId of kidIds) {
                try {
                    await checkChallenge('account_created', kidId);
                } catch (gamificationError) {
                    // Log but don't fail - gamification is secondary to account creation
                    console.warn('Gamification check failed for user:', kidId, gamificationError);
                }
            }

            success(t('onboarding.kids.profiles_updated_success', { count: validKids.length }));
            onNext(kidIds);
        } catch (error) {
            console.error('Profile save error:', error);
            console.error('Error details:', JSON.stringify(error, Object.getOwnPropertyNames(error)));
            showError(t('onboarding.kids.profiles_save_failed'));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="space-y-6">
            <div className="text-center mb-8">
                <h2 className="text-2xl font-bold">{t('onboarding.kids.who_training')}</h2>
                <p className="text-muted-foreground">{t('onboarding.kids.add_family_subtitle')}</p>
            </div>

            <div className="space-y-4">
                {kids.map((kid, idx) => (
                    <div key={idx} className="flex flex-col gap-4 p-4 bg-gray-50 rounded-lg animate-in fade-in slide-in-from-bottom-2 border">
                        {/* Row 1: Type Selection */}
                        <div className="flex justify-between items-center pb-2 border-b border-gray-100">
                            <div className="flex items-center gap-2">
                                <Label className="text-xs text-muted-foreground uppercase tracking-wide">{t('onboarding.subscription.member_number', { num: idx + 1 })}</Label>
                            </div>
                            {kids.length > 1 && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 text-red-500 hover:text-red-700 hover:bg-red-50"
                                    onClick={() => removeKidRow(idx)}
                                >
                                    <Trash2 className="w-3 h-3 mr-1" /> {t('onboarding.kids.remove')}
                                </Button>
                            )}
                        </div>

                        {/* Row 2: Fields */}
                        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                            <div className="md:col-span-3">
                                <Label className="text-xs mb-1.5 block">{t('onboarding.kids.type_label')}</Label>
                                <Select value={kid.type} onValueChange={(val: any) => updateKid(idx, 'type', val)}>
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="child">{t('onboarding.kids.type.child')}</SelectItem>
                                        <SelectItem value="adult">{t('onboarding.kids.type.adult')}</SelectItem>
                                        <SelectItem value="myself">{t('onboarding.kids.type.myself')}</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="md:col-span-3">
                                <Label className="text-xs mb-1.5 block">{t('common.name')}</Label>
                                <Input
                                    value={kid.name}
                                    onChange={(e) => updateKid(idx, 'name', e.target.value)}
                                    placeholder={t('onboarding.kids.full_name_placeholder')}
                                    disabled={kid.type === 'myself'} // Name locked to account name for simplicity, or allowed? typical to lock or prefill
                                />
                            </div>
                            <div className="md:col-span-2">
                                <Label className="text-xs mb-1.5 block">{t('profile.gender')}</Label>
                                <Select value={kid.gender} onValueChange={(val) => updateKid(idx, 'gender', val)}>
                                    <SelectTrigger>
                                        <SelectValue placeholder={t('onboarding.kids.select_placeholder')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="male">{t('profile.male')}</SelectItem>
                                        <SelectItem value="female">{t('profile.female')}</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="md:col-span-12">
                                <PlanSelect
                                    plans={plans}
                                    loading={plansLoading}
                                    failed={plansFailed}
                                    onRetry={retryPlans}
                                    value={kid.planId}
                                    onChange={v => updateKid(idx, 'planId', v)}
                                    idPrefix={`kid-plan-${idx}`}
                                    label={t('signup.plan.label_for_child')}
                                />
                            </div>

                            <div className="md:col-span-2">
                                <Label className="text-xs mb-1.5 block">{t('edit_kid.age')}</Label>
                                <Input
                                    type="number"
                                    value={kid.age}
                                    onChange={(e) => updateKid(idx, 'age', e.target.value)}
                                    placeholder={t('edit_kid.age')}
                                />
                            </div>
                            <div className="md:col-span-2">
                                <Label className="text-xs mb-1.5 block">{t('belt.belt')}</Label>
                                <Select value={kid.belt} onValueChange={(val) => updateKid(idx, 'belt', val)}>
                                    <SelectTrigger>
                                        <SelectValue placeholder={t('belt.belt')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="White">{t('onboarding.kids.belt.white')}</SelectItem>
                                        <SelectItem value="Grey White">{t('onboarding.kids.belt.grey_white')}</SelectItem>
                                        <SelectItem value="Grey">{t('onboarding.kids.belt.grey')}</SelectItem>
                                        <SelectItem value="Grey Black">{t('onboarding.kids.belt.grey_black')}</SelectItem>
                                        <SelectItem value="Yellow White">{t('onboarding.kids.belt.yellow_white')}</SelectItem>
                                        <SelectItem value="Yellow">{t('onboarding.kids.belt.yellow')}</SelectItem>
                                        <SelectItem value="Yellow Black">{t('onboarding.kids.belt.yellow_black')}</SelectItem>
                                        <SelectItem value="Orange White">{t('onboarding.kids.belt.orange_white')}</SelectItem>
                                        <SelectItem value="Orange">{t('onboarding.kids.belt.orange')}</SelectItem>
                                        <SelectItem value="Orange Black">{t('onboarding.kids.belt.orange_black')}</SelectItem>
                                        <SelectItem value="Green White">{t('onboarding.kids.belt.green_white')}</SelectItem>
                                        <SelectItem value="Green">{t('onboarding.kids.belt.green')}</SelectItem>
                                        <SelectItem value="Green Black">{t('onboarding.kids.belt.green_black')}</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <Button
                variant="outline"
                onClick={addKidRow}
                className="w-full border-dashed"
            >
                <UserPlus className="w-4 h-4 mr-2" /> {t('onboarding.kids.add_another')}
            </Button>

            <div className="pt-6 flex justify-between items-center">
                <Button variant="ghost" onClick={() => onNext()}>
                    {t('onboarding.classes.skip_for_now')}
                </Button>
                <Button onClick={handleSubmit} disabled={isSubmitting} size="lg">
                    {isSubmitting ? t('onboarding.kids.saving') : t('onboarding.subscription.continue')}
                </Button>
            </div>
        </div>
    );
};
